import { InlineLoading, InlineNotification } from '@carbon/react';
import { ActionBar } from './action-bar';
import { ZeroSumInfoBanner } from './zero-sum-info-banner';
import { AggregatedFreeCashFlow } from '../evaluation-result/aggregated-free-cash-flow/aggregated-free-cash-flow';
import type {
  AggregatedFreeCashFlow as AggregatedFreeCashFlowType,
  FreeCashFlowAggregationPeriod,
  MeasurementType,
  SlotVolumeSource,
  SlotAfeSource,
} from 'types/evaluation';
import type {
  SlotVolumeSourceConfiguration,
  SlotAfeSourceConfiguration,
  SlotCostDateConfiguration,
  ConfigUpdater,
} from '../evaluation-result/aggregated-free-cash-flow/types';
import type {
  Eval2Run,
  ScheduledSlotDates,
  SlotDateOverride,
  TaxScenarioItem,
} from './types';
import type { AppNotification, DpsScheduleStatus } from './atoms';
import type { ScenariosResponse } from 'types/api-responses';
import type { StatusItem } from 'pages/crm-tracts-subtracts/types';
import style from './eval-nav-layout.module.scss';

const EMPTY_RESULT: AggregatedFreeCashFlowType = {
  freeCashflows: [],
  interval: 'year',
  effectiveDate: new Date().toISOString().slice(0, 10),
  productionVolumes: [],
  packageOverview: { maxPricing: [], targetPricing: [] },
  rescatSlotCount: [],
};

interface EvalMainContentProps {
  // ActionBar
  isLeftPanelCollapsed: boolean;
  onToggleLeftPanel: () => void;
  onRename: () => void;
  isRenameVisible: boolean;
  onSave: () => void;
  onCalculate: () => void;
  isSaveVisible: boolean;
  isSaveDisabled: boolean;
  isSaveLoading: boolean;
  isCalculateDisabled: boolean;
  calculateDisabledReason?: string;
  isCalculateLoading: boolean;
  /** Gates the agent trigger in the action bar; the dock itself is mounted by
   * EvalNavLayout, which owns the workspace the conversation belongs to. */
  isAgentAvailable?: boolean;
  /** The DCA scenario the eval shows (the selected case's); a new DCA workspace opens on it. */
  dcaScenarioId?: number | null;
  // Banner
  isZeroSum: boolean;
  /** Whether config has changed since last calculation. */
  isDirty: boolean;
  // Notifications
  notifications: AppNotification[];
  onDismissNotification: (id: string) => void;
  // AggregatedFreeCashFlow — all direct props
  packageId: number;
  evalId: number | null;
  scenarioHref: string;
  scenarioName?: string;
  resultData: AggregatedFreeCashFlowType | null;
  isResultLoading: boolean;
  resultError: string;
  effectiveDate: string | null;
  setEffectiveDate: (date: string | null) => void;
  aggregation: FreeCashFlowAggregationPeriod;
  setAggregation: (agg: FreeCashFlowAggregationPeriod) => void;
  measurementType: MeasurementType;
  setMeasurementType: (mt: MeasurementType) => void;
  slotVolumeSource: SlotVolumeSource;
  setSlotVolumeSource: (src: SlotVolumeSource) => void;
  slotAfeSource: SlotAfeSource;
  setSlotAfeSource: (src: SlotAfeSource) => void;
  slotVolumeConfigurations: Record<string, SlotVolumeSourceConfiguration>;
  setSlotVolumeConfigurations: ConfigUpdater<SlotVolumeSourceConfiguration>;
  slotAfeConfigurations: Record<string, SlotAfeSourceConfiguration>;
  setSlotAfeConfigurations: ConfigUpdater<SlotAfeSourceConfiguration>;
  slotCostDateConfigurations: Record<string, SlotCostDateConfiguration>;
  setSlotCostDateConfigurations: ConfigUpdater<SlotCostDateConfiguration>;
  scheduledDates?: Record<string, ScheduledSlotDates>;
  slotDateConfigurations?: Record<string, SlotDateOverride>;
  setSlotDateConfigurations?: ConfigUpdater<SlotDateOverride>;
  // Legacy eval2 props — `eval2Runs`/`selectedEval2Id` are still threaded
  // through for read-only display surfaces (e.g. comparison panels). The
  // active source-of-truth picker is now `scenarios` (INS-8827).
  eval2Runs?: Eval2Run[];
  selectedEval2Id?: number | null;
  scenarios?: ScenariosResponse[];
  selectedScenarioId?: number | null;
  onScenarioChange?: (id: number | null) => void;
  dpsList?: Array<{
    id: number;
    name: string;
    paceModel: string;
    referenceDate: string;
  }>;
  selectedDpsId?: number | null;
  onDpsChange?: (id: number) => void;
  drsList?: Array<{
    id: number;
    name: string;
    effectiveDate: string | null;
  }>;
  selectedDrsId?: number | null;
  onDrsChange?: (id: number | null) => void;
  differentialScenarioList?: Array<{
    id: number;
    name: string;
    effectiveDate: string | null;
  }>;
  selectedDifferentialScenarioId?: number | null;
  onDifferentialScenarioChange?: (id: number | null) => void;
  taxScenarioList?: TaxScenarioItem[];
  selectedTaxScenarioId?: number | null;
  onTaxScenarioChange?: (id: number | null) => void;
  expenseScenarioList?: Array<{
    id: number;
    name: string;
    effectiveDate: string | null;
  }>;
  selectedExpenseScenarioId?: number | null;
  onExpenseScenarioChange?: (id: number | null) => void;
  evalMode?: 'case' | 'legacy';
  onEvalModeChange?: (mode: 'case' | 'legacy') => void;
  evaluationCaseList?: {
    id: number;
    name: string;
    evaluationType: string;
    scope: string | null;
  }[];
  selectedEvaluationCaseId?: number | null;
  onEvaluationCaseChange?: (id: number | null) => void;
  evaluationCaseDetail?: any;
  dpsSlotCount?: number | null;
  dpsScheduleStatus?: DpsScheduleStatus;
  snapshotStatusItems?: StatusItem[];
  snapshotOverallStatus?: string;
  calculationId?: string;
  evaluationCalculationId?: number | null;
  /** The DCA a completed run of the selected case ran with; undefined when none is shown. */
  runDcaScenarioId?: number | null;
  evalExpiration?: string | null;
  evalDate?: string | null;
  /** Whether initial page data (workspaces, calculations, detail) is loading. */
  isPageLoading?: boolean;
  partiallyCoveredWellsHref: string;
  partiallyCoveredWellsCount: number | null;
  calculationWarnings?: string[];
  onExportAries?: () => void;
  isExportAriesDisabled?: boolean;
  isExportAriesLoading?: boolean;
  exportAriesTooltip?: string;
  // Aries MDB export. `onExportAriesMdb` opens the config modal (INS-10553);
  // run progress is shown inside the parent-owned modal.
  onExportAriesMdb?: () => void;
}

/**
 * Presentational wrapper for the main content area of the Eval 3.0 workspace.
 * Renders the ActionBar, optional ZeroSumInfoBanner, and either the
 * AggregatedFreeCashFlow view or an empty placeholder.
 *
 * @param {EvalMainContentProps} props - Component props.
 * @return {JSX.Element} The main content area.
 */
export const EvalMainContent = ({
  isLeftPanelCollapsed,
  onToggleLeftPanel,
  onRename,
  isRenameVisible,
  onSave,
  onCalculate,
  isSaveVisible,
  isSaveDisabled,
  isSaveLoading,
  isCalculateDisabled,
  calculateDisabledReason,
  isCalculateLoading,
  isAgentAvailable,
  dcaScenarioId,
  isZeroSum,
  isDirty,
  notifications,
  onDismissNotification,
  packageId,
  evalId,
  scenarioHref,
  scenarioName,
  resultData,
  isResultLoading,
  resultError,
  effectiveDate,
  setEffectiveDate,
  aggregation,
  setAggregation,
  measurementType,
  setMeasurementType,
  slotVolumeSource,
  setSlotVolumeSource,
  slotAfeSource,
  setSlotAfeSource,
  slotVolumeConfigurations,
  setSlotVolumeConfigurations,
  slotAfeConfigurations,
  setSlotAfeConfigurations,
  slotCostDateConfigurations,
  setSlotCostDateConfigurations,
  scheduledDates,
  slotDateConfigurations,
  setSlotDateConfigurations,
  eval2Runs,
  selectedEval2Id,
  scenarios,
  selectedScenarioId,
  onScenarioChange,
  dpsList,
  selectedDpsId,
  onDpsChange,
  drsList,
  selectedDrsId,
  onDrsChange,
  differentialScenarioList,
  selectedDifferentialScenarioId,
  onDifferentialScenarioChange,
  taxScenarioList,
  selectedTaxScenarioId,
  onTaxScenarioChange,
  expenseScenarioList,
  selectedExpenseScenarioId,
  onExpenseScenarioChange,
  evalMode,
  onEvalModeChange,
  evaluationCaseList,
  selectedEvaluationCaseId,
  onEvaluationCaseChange,
  evaluationCaseDetail,
  dpsSlotCount,
  dpsScheduleStatus,
  snapshotStatusItems,
  snapshotOverallStatus,
  calculationId,
  evaluationCalculationId,
  runDcaScenarioId,
  evalExpiration,
  evalDate,
  isPageLoading,
  partiallyCoveredWellsHref,
  partiallyCoveredWellsCount,
  calculationWarnings,
  onExportAries,
  isExportAriesDisabled,
  isExportAriesLoading,
  exportAriesTooltip,
  onExportAriesMdb,
}: EvalMainContentProps): JSX.Element => {
  return (
    <div className={style.mainContent}>
      <ActionBar
        isLeftPanelCollapsed={isLeftPanelCollapsed}
        onToggleLeftPanel={onToggleLeftPanel}
        onRename={onRename}
        isRenameVisible={isRenameVisible}
        onSave={onSave}
        onCalculate={onCalculate}
        isSaveVisible={isSaveVisible}
        isSaveDisabled={isSaveDisabled}
        isSaveLoading={isSaveLoading}
        isCalculateDisabled={isCalculateDisabled}
        calculateDisabledReason={calculateDisabledReason}
        isCalculateLoading={isCalculateLoading}
        packageId={packageId}
        isAgentAvailable={isAgentAvailable}
        dcaScenarioId={dcaScenarioId}
      />

      {notifications.map((n) => (
        <InlineNotification
          key={n.id}
          kind="error"
          title={n.message}
          lowContrast
          onCloseButtonClick={() => onDismissNotification(n.id)}
        />
      ))}

      <ZeroSumInfoBanner isVisible={isZeroSum} />

      {isDirty && !isZeroSum && (
        <InlineNotification
          kind="info"
          title="Changes detected"
          subtitle="You've updated settings, but the results shown are from the last calculation. Click Calculate to update results. Clicking Save will save the results currently shown."
          lowContrast
          hideCloseButton
          className={style.dirtyNotification}
        />
      )}

      {resultError && (
        <InlineNotification kind="error" title={resultError} lowContrast />
      )}

      {isPageLoading && (
        <InlineLoading
          description="Loading evaluation..."
          style={{ paddingInline: '1rem' }}
        />
      )}

      {!isPageLoading && (
        <AggregatedFreeCashFlow
          evalId={evalId ?? 0}
          packageId={packageId}
          scenarioHref={scenarioHref}
          scenarioName={scenarioName}
          resultData={resultData ?? EMPTY_RESULT}
          isLoading={isResultLoading}
          effectiveDate={effectiveDate}
          setEffectiveDate={setEffectiveDate}
          aggregation={aggregation}
          setAggregation={setAggregation}
          measurementType={measurementType}
          setMeasurementType={setMeasurementType}
          slotVolumeSource={slotVolumeSource}
          setSlotVolumeSource={setSlotVolumeSource}
          slotAfeSource={slotAfeSource}
          setSlotAfeSource={setSlotAfeSource}
          slotVolumeConfigurations={slotVolumeConfigurations}
          setSlotVolumeConfigurations={setSlotVolumeConfigurations}
          slotAfeConfigurations={slotAfeConfigurations}
          setSlotAfeConfigurations={setSlotAfeConfigurations}
          slotCostDateConfigurations={slotCostDateConfigurations}
          setSlotCostDateConfigurations={setSlotCostDateConfigurations}
          scheduledDates={scheduledDates}
          slotDateConfigurations={slotDateConfigurations}
          setSlotDateConfigurations={setSlotDateConfigurations}
          eval2Runs={eval2Runs}
          selectedEval2Id={selectedEval2Id}
          scenarios={scenarios}
          selectedScenarioId={selectedScenarioId}
          onScenarioChange={onScenarioChange}
          dpsList={dpsList}
          selectedDpsId={selectedDpsId}
          onDpsChange={onDpsChange}
          drsList={drsList}
          selectedDrsId={selectedDrsId}
          onDrsChange={onDrsChange}
          differentialScenarioList={differentialScenarioList}
          selectedDifferentialScenarioId={selectedDifferentialScenarioId}
          onDifferentialScenarioChange={onDifferentialScenarioChange}
          taxScenarioList={taxScenarioList}
          selectedTaxScenarioId={selectedTaxScenarioId}
          onTaxScenarioChange={onTaxScenarioChange}
          expenseScenarioList={expenseScenarioList}
          selectedExpenseScenarioId={selectedExpenseScenarioId}
          onExpenseScenarioChange={onExpenseScenarioChange}
          evalMode={evalMode}
          onEvalModeChange={onEvalModeChange}
          evaluationCaseList={evaluationCaseList}
          selectedEvaluationCaseId={selectedEvaluationCaseId}
          onEvaluationCaseChange={onEvaluationCaseChange}
          evaluationCaseDetail={evaluationCaseDetail}
          dpsSlotCount={dpsSlotCount}
          dpsScheduleStatus={dpsScheduleStatus}
          snapshotStatusItems={snapshotStatusItems}
          snapshotOverallStatus={snapshotOverallStatus}
          calculationId={calculationId}
          evaluationCalculationId={evaluationCalculationId}
          runDcaScenarioId={runDcaScenarioId}
          evalExpiration={evalExpiration}
          evalDate={evalDate}
          partiallyCoveredWellsHref={partiallyCoveredWellsHref}
          partiallyCoveredWellsCount={partiallyCoveredWellsCount}
          calculationWarnings={calculationWarnings}
          onExportAries={onExportAries}
          isExportAriesDisabled={isExportAriesDisabled}
          isExportAriesLoading={isExportAriesLoading}
          exportAriesTooltip={exportAriesTooltip}
          onExportAriesMdb={onExportAriesMdb}
        />
      )}
    </div>
  );
};
